import { useCallback, useContext, useEffect, useRef } from 'react';
import GlobalContext from '../context/global-context';
import { BaseHTTP } from '../api/base.http';
import { BUK_SYNC_STARTED_EVENT, clearBukSyncPending, isBukSyncPending } from '../utils/bukSyncFlag';
import { notifyBukSyncCompleted, notifyBukSyncError } from '../utils/bukSyncNotifications';

const BukSyncNotifier = () => {
  const { currentBusiness } = useContext(GlobalContext);
  const activeBusinessIdsRef = useRef(new Set());

  const watch = useCallback((businessId) => {
    if (!businessId || activeBusinessIdsRef.current.has(businessId)) return;
    activeBusinessIdsRef.current.add(businessId);

    let attempts = 0;
    let timer = null;

    const stop = () => {
      if (timer) clearTimeout(timer);
      activeBusinessIdsRef.current.delete(businessId);
    };

    const poll = async () => {
      attempts += 1;
      try {
        const http = BaseHTTP.getInstance().http;
        const response = await http.get(`v1/business/${businessId}/integrations/buk/status`);
        const data = response.data;

        if (data.status === 'completed') {
          clearBukSyncPending(businessId);
          notifyBukSyncCompleted(data);
          stop();
          return;
        }
        if (data.status === 'error') {
          clearBukSyncPending(businessId);
          notifyBukSyncError(data);
          stop();
          return;
        }
        if (data.status !== 'processing') {
          clearBukSyncPending(businessId);
          stop();
          return;
        }
      } catch (err) {
        console.error('Error checking Buk sync status (background watcher):', err);
      }

      const baseInterval = 5000;
      const maxInterval = 20000;
      const backoffMultiplier = 1.2;
      const interval = Math.min(baseInterval * Math.pow(backoffMultiplier, attempts - 1), maxInterval);
      timer = setTimeout(poll, interval);
    };

    poll();
  }, []);

  useEffect(() => {
    const businessId = currentBusiness?.id;
    if (businessId && isBukSyncPending(businessId)) {
      watch(businessId);
    }
  }, [currentBusiness?.id, watch]);

  useEffect(() => {
    const handleSyncStarted = (event) => {
      const businessId = event?.detail?.businessId;
      if (businessId) watch(businessId);
    };
    window.addEventListener(BUK_SYNC_STARTED_EVENT, handleSyncStarted);
    return () => window.removeEventListener(BUK_SYNC_STARTED_EVENT, handleSyncStarted);
  }, [watch]);

  return null;
};

export default BukSyncNotifier;
